import {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import type { CSSProperties } from "react";
import type {
  FilePreviewProps,
  PdfPreviewerRef
} from "../../types/FilePreviewerTypes";
import { getFileBlobURL } from "@certa/queries/services/common.services";
import { ToastTypes, showToast } from "@certa/catalyst/components/Toast";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Loader } from "@certa/blocks";
import { Text } from "@certa/blocks/thanos";
import { motion } from "framer-motion";
import loadable from "@loadable/component";
import type { DocumentProps } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { FallbackLoader } from "@certa/common/components/FallbackLoader";
import { useSearch } from "./Search/Search.hook";
import {
  DOCUMENT_TOP_BOTTOM_PADDING,
  HighlightLayer
} from "./Search/HighlightLayer";
import {
  normalizeSingleKeyword,
  findTextItemRanges
} from "./Search/Search.utils";
import { css, cx } from "emotion";
import { Toolbar } from "./Toolbar";
import { TOOLBAR_MARGIN_TOP } from "./Toolbar/Toolbar.constants";
import {
  CustomHighlightLayer,
  type CustomHighlightArea
} from "./Search/CustomHighlightLayer";
import type { PageLoadSuccessParams, TextItem } from "./Search/Search.types";

const SCROLL_BUFFER = 36;

const pageStyle = css({
  "> canvas, div": {
    margin: "0 auto"
  }
});
const pageStyleFullScreen = cx(
  pageStyle,
  css({
    boxShadow: "4px 5px 10px 0px rgb(48, 48, 48)"
  })
);

const documentContainerFullScreenStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  padding: `${DOCUMENT_TOP_BOTTOM_PADDING} 0`,
  marginTop: TOOLBAR_MARGIN_TOP
};
const documentFullScreenStyle = css({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  alignItems: "center",
  width: "min-content"
});

const Document = loadable(
  () =>
    import("react-pdf").then(module => {
      // For React-PDF to work, PDF.js worker needs to be provided.
      // https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#import-worker-recommended
      module.pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
      ).toString();
      return module.Document;
    }),
  {
    fallback: <FallbackLoader />
  }
);

const Page = loadable(() => import("react-pdf").then(module => module.Page), {
  fallback: <FallbackLoader />
});

const documentOptions: DocumentProps["options"] = {
  // `isEvalSupported` is set to false to prevent the following security vulnerability:
  // https://github.com/advisories/GHSA-wgrm-67xf-hhpq
  isEvalSupported: false
};

const PdfPreviewer = forwardRef<
  PdfPreviewerRef,
  FilePreviewProps & {
    canFetchExternalFiles?: boolean;
  } & {
    // External highlight irrespective of the search.
    highlightConfig?: {
      text?: string;
      pageIndex: number;
    };
  }
>((props, ref) => {
  const {
    url,
    highlightedText,
    filePreviewDataSource,
    fileName,
    onDownload,
    showToolbar: shouldShowToolbar = false,
    isFullScreen = false,
    canFetchExternalFiles = false,
    highlightConfig
  } = props;

  const handleItemClick = ({ pageNumber }: { pageNumber: number }) => {
    // Scroll to the clicked page
    if (pageRefs.current[pageNumber]) {
      pageRefs.current[pageNumber].scrollIntoView({ behavior: "smooth" });
    }
  };

  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Expose scrollToPage method via ref
  useImperativeHandle(ref, () => ({
    scrollToPage: handleItemClick
  }));

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerParentRef = useRef<HTMLDivElement>(null);
  const [initialPageSize, setInitialPageSize] = useState<
    | {
        width: number;
        height: number;
      }
    | undefined
  >(undefined);
  const [totalNoOfPages, setTotalNoOfPages] = useState(0);
  const { highlightAreasMapping, pageDimensions, onGetTextSuccessPage } =
    useSearch({
      initialKeywordRegexp: highlightedText
        ? [normalizeSingleKeyword(highlightedText)]
        : undefined
    });

  const [fileBlobURL, setFileBlobURL] = useState<null | string>(null);

  const [pageScale, setPageScale] = useState<number | null>(null);

  const [clientHeight, setClientHeight] = useState(
    document.documentElement.clientHeight
  );
  const [clientWidth, setClientWidth] = useState(
    document.documentElement.clientWidth
  );

  const [customHighlightAreas, setCustomHighlightAreas] = useState<
    CustomHighlightArea[]
  >([]);

  // Store page data for recalculation when highlightConfig changes
  const pageDataRef = useRef<
    Record<
      number,
      {
        height: number;
        width: number;
        textItems: TextItem[];
      }
    >
  >({});

  // Update clientHeight on resize
  useEffect(() => {
    const handleResize = () => {
      setClientHeight(document.documentElement.clientHeight);
      setClientWidth(document.documentElement.clientWidth);
      setPageScale(null);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Calculate container height based on clientHeight (75vh)
  const containerHeight = isFullScreen ? clientHeight : clientHeight * 0.75; // 75vh
  const containerWidth =
    (isFullScreen ? clientWidth : clientWidth * 0.75) - SCROLL_BUFFER; // 75vh - padding

  /*
  - Why 5 status in below loading status?
  - "idle" - initial state
  - "loading" - fetching file blob url
  - "pdf-loading" - pdf is loading
  - "error" - error while fetching file blob url
  - "success" - file blob url fetched successfully
  */
  const [fetchingFileStatus, setFetchingFileStatus] = useState<
    "idle" | "loading" | "success" | "error" | "pdf-loading"
  >("idle");

  const fetchAndStoreBlobURL = useCallback(async () => {
    // Preload the PDF renderer component while we fetch the pdf file
    Document.preload();
    Page.preload();

    try {
      setFetchingFileStatus("loading");
      const fileBlobURL = await getFileBlobURL(
        url,
        filePreviewDataSource,
        canFetchExternalFiles
      );
      if (fileBlobURL) {
        setFetchingFileStatus("pdf-loading");
        setFileBlobURL(fileBlobURL);
      }
    } catch (e) {
      setFetchingFileStatus("error");
      showToast({
        title: "Failed to fetch PDF file",
        type: ToastTypes.ERROR
      });
    }
  }, [url, filePreviewDataSource, canFetchExternalFiles]);

  const onLoadErrorFromLib = (err: Error) => {
    setFetchingFileStatus("error");
    showToast({
      title: err.message,
      type: ToastTypes.ERROR
    });
  };

  useEffect(() => {
    if (url) {
      fetchAndStoreBlobURL();
    }
  }, [url, fetchAndStoreBlobURL]);

  const onPageLoad = ({ width, height }: { width: number; height: number }) => {
    if (pageScale !== null) {
      return;
    }
    setInitialPageSize({ width, height });
    const widthScale = Math.floor((containerWidth / width) * 100) / 100;
    const heightScale = Math.floor((containerHeight / height) * 100) / 100;

    const acceptedPageScale = width < height ? widthScale : heightScale;

    // Max we set to 100%
    const finalScale = Math.min(1, acceptedPageScale);

    setPageScale(finalScale);
  };

  const calculateHighlights = useCallback((text: string, pageIndex: number) => {
    const pageData = pageDataRef.current[pageIndex];

    if (!pageData || !pageData.textItems.length) {
      return;
    }

    const matchedRanges = findTextItemRanges(pageData.textItems, text);

    if (matchedRanges.length > 0) {
      setCustomHighlightAreas([
        {
          pageIndex,
          pageHeight: pageData.height,
          pageWidth: pageData.width,
          textItems: matchedRanges.map(range => ({
            item: range.item,
            matchStartInItem: range.matchStartInItem,
            matchEndInItem: range.matchEndInItem
          }))
        }
      ]);
    } else {
      // Clear highlights if no match found
      setCustomHighlightAreas([]);
    }
  }, []);

  const handlePageTextContentReady = useCallback(
    async (pageProxy: PageLoadSuccessParams, pageIndex: number) => {
      const textContents = await pageProxy.getTextContent();
      const filteredTextContents = textContents.items
        .filter((item): item is TextItem => "str" in item)
        .filter(item => item.str.trim() !== "");

      // Store in ref for recalculation when highlightConfig changes
      pageDataRef.current[pageIndex] = {
        height: pageProxy.height,
        width: pageProxy.width,
        textItems: filteredTextContents
      };

      // If this is the page we need to highlight, calculate now
      if (highlightConfig?.pageIndex === pageIndex && highlightConfig?.text) {
        calculateHighlights(highlightConfig.text, pageIndex);
      }
    },
    [highlightConfig?.pageIndex, highlightConfig?.text, calculateHighlights]
  );

  useEffect(() => {
    if (highlightConfig?.text && highlightConfig?.pageIndex !== undefined) {
      calculateHighlights(highlightConfig.text, highlightConfig.pageIndex);
    } else {
      // Clear highlights if no config
      setCustomHighlightAreas([]);
    }
  }, [highlightConfig?.text, highlightConfig?.pageIndex, calculateHighlights]);

  const loaderContent = (
    <Stack align="center" justify="center" width="100%">
      <Loader size="large" />
    </Stack>
  );

  const getPdfPreviewBodyAsPerStatus = () => {
    switch (fetchingFileStatus) {
      case "loading":
        return loaderContent;
      case "error":
        return <Text>Failed to load pdf</Text>;
      case "pdf-loading":
      case "success":
        return (
          <>
            {shouldShowToolbar && (
              <Toolbar
                pageScale={pageScale}
                pdfContainerRef={pdfContainerRef}
                pdfContainerParentRef={pdfContainerParentRef}
                onDownload={onDownload}
                fileName={fileName}
                totalNoOfPages={totalNoOfPages}
                pageDimensions={pageDimensions}
                handleScaleChange={setPageScale}
                initialPageSize={initialPageSize}
              />
            )}
            <motion.div
              ref={pdfContainerParentRef}
              initial={{ height: 0 }}
              animate={{ height: containerHeight }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
              className={css({
                height: 0,
                "overflow-y": "auto",
                "overflow-x": "auto",
                opacity: fetchingFileStatus === "pdf-loading" ? 0 : 1
              })}
              style={{
                width: "100%",
                position: "relative",
                alignItems: "center",
                ...(isFullScreen ? documentContainerFullScreenStyle : {})
              }}
            >
              <Document
                file={fileBlobURL}
                onLoadSuccess={({ numPages }) => {
                  setFetchingFileStatus("success");
                  setTotalNoOfPages(numPages);
                }}
                loading={loaderContent}
                onLoadError={onLoadErrorFromLib}
                onSourceError={onLoadErrorFromLib}
                options={documentOptions}
                className={isFullScreen ? documentFullScreenStyle : undefined}
              >
                {Array.from(new Array(totalNoOfPages)).map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <div
                      key={pageNumber}
                      ref={el => {
                        pageRefs.current[pageNumber] = el;
                      }}
                    >
                      <Page
                        key={index}
                        pageNumber={pageNumber}
                        onLoadSuccess={async props => {
                          const pageIndex = pageNumber - 1;
                          onPageLoad(props);
                          handlePageTextContentReady(props, pageIndex);
                        }}
                        scale={pageScale || 1}
                        onGetTextSuccess={() => onGetTextSuccessPage(index)}
                        className={[
                          isFullScreen ? pageStyleFullScreen : pageStyle
                        ]}
                      />
                    </div>
                  );
                })}
              </Document>
              {/* If external highlightConfig is provided, we use CustomHighlightLayer, otherwise we use HighlightLayer */}
              {highlightConfig ? (
                <CustomHighlightLayer
                  ref={pdfContainerRef}
                  totalNoOfPages={totalNoOfPages}
                  pageDimensions={pageDimensions}
                  highlightAreas={customHighlightAreas}
                  scale={pageScale || 1}
                />
              ) : (
                <HighlightLayer
                  ref={pdfContainerRef}
                  totalNoOfPages={totalNoOfPages}
                  pageDimensions={pageDimensions}
                  highlightAreasMapping={highlightAreasMapping}
                />
              )}
            </motion.div>
          </>
        );
      case "idle":
        return null;
    }
  };

  return getPdfPreviewBodyAsPerStatus();
});

export { PdfPreviewer };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(PdfPreviewer as any).mimeTypes = ["application/pdf"];
